import React, { Fragment, useEffect, useState } from 'react'
import clsx from 'clsx'
import {
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  Button,
} from '@material-ui/core'
import {
  AlertCircle as AlertCircleIcon,
  ThumbsUp as ThumbsUpIcon,
} from 'react-feather'

import { Dai } from 'components'
import { FeedbackDialog, MetList, DownloadStatement } from './components'

import useSnackbar from 'hooks/useSnackbar'
import useDai from 'hooks/useDai'

import useStyles from './styles'
import * as service from 'service'
import helpers from 'helpers'

const ActivityAnalysisDai = ({
  startDaiAnalysis,
  governanceProjectActivity,
  governanceProjectId,
  setStartDaiAnalysis,
  refresh,
}) => {
  const [loading, setLoading] = useState(false)
  const [showResponse, setShowResponse] = useState(true)
  const [daiResponse, setDaiResponse] = useState(
    governanceProjectActivity?.daiResponse?.response?.body?.formattedResponse ||
      null,
  )
  const [openFeedback, setOpenFeedback] = useState(false)

  const { expandedDaiActivityAnalysis, setExpandedDaiActivityAnalysis } =
    useDai()

  const classes = useStyles()
  const snackbar = useSnackbar()
  const theme = useTheme()

  const defaultDaiResponse = governanceProjectActivity?.daiResponse

  const analysisDate = daiResponse?.createdAt || defaultDaiResponse?.createdAt

  const adhesionOptions =
    governanceProjectActivity?.governanceProjectQuestion?.adhesionOptions || []
  const adhesionColor = helpers.governanceProject.getAdhesionColor(
    daiResponse,
    adhesionOptions,
    theme,
  )

  const onSubmit = async () => {
    setLoading(true)
    setExpandedDaiActivityAnalysis(true)
    try {
      const response =
        await service.dponet.governanceProjectActivity.daiAnalysis({
          governanceProjectId,
          id: governanceProjectActivity.id,
        })
      const daiResponse = response.data?.daiResponse
      setDaiResponse(daiResponse)
      setShowResponse(true)
      refresh()
    } catch (error) {
      setShowResponse(false)
      setDaiResponse(null)
      refresh()

      snackbar.open({
        message:
          helpers.formatters.errorMessage(error) ||
          'Nenhuma resposta da DAI foi recebida.',
        variant: 'error',
      })
    } finally {
      setStartDaiAnalysis(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (startDaiAnalysis) {
      onSubmit()
    }
  }, [startDaiAnalysis])

  if (!daiResponse && !loading) return <></>

  const hasAdhesion = !!daiResponse?.adhesion
  const hasJustification = !!daiResponse?.details?.justification
  const hasRequirements = daiResponse?.details?.metRequirements?.length > 0
  const hasEvidences = daiResponse?.details?.metEvidences?.length > 0
  const hasGuidelines = daiResponse?.details?.improvementGuidelines?.length > 0

  return (
    <Dai.Card
      title="Declaração de aplicabilidade (SoA)"
      expandable
      expanded={expandedDaiActivityAnalysis}
      onToggleExpand={() =>
        setExpandedDaiActivityAnalysis(!expandedDaiActivityAnalysis)
      }
      headerContent={
        showResponse &&
        !loading && (
          <Typography variant="body2" className={classes.dateText}>
            Analisado em: {helpers.formatters.ptBrFormatDateTime(analysisDate)}
          </Typography>
        )
      }
    >
      {loading && (
        <Dai.Loading
          text="Analisando o controle e as evidências com a DAI..."
          loading
        />
      )}

      {showResponse && !loading && (
        <Box>
          <Box className={classes.responseContainer}>
            {hasAdhesion && (
              <Box className={classes.adhesionSection}>
                <Typography
                  variant="h6"
                  className={classes.sectionTitle}
                  gutterBottom
                >
                  Nível de Aderência
                </Typography>
                <Box className={classes.adhesionContent}>
                  <Chip
                    label={daiResponse.adhesion.adhesionLetter}
                    className={classes.adhesionChip}
                    style={{
                      backgroundColor: adhesionColor,
                      color: theme.palette.getContrastText(adhesionColor),
                    }}
                  />
                  <Typography variant="body1" className={classes.adhesionLevel}>
                    {daiResponse.adhesion.adhesionLevel}
                  </Typography>
                </Box>
              </Box>
            )}

            {hasAdhesion &&
              (hasJustification || hasRequirements || hasGuidelines) && (
                <Divider className={classes.divider} />
              )}

            {hasJustification && (
              <Box>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Justificativa
                </Typography>
                <Typography
                  variant="body2"
                  className={classes.justificationText}
                >
                  {daiResponse.details.justification}
                </Typography>
              </Box>
            )}

            {hasJustification && (hasRequirements || hasGuidelines) && (
              <Divider className={classes.divider} />
            )}

            {hasRequirements && (
              <Box className={classes.requirementsSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Requisitos Avaliados
                </Typography>

                <MetList
                  mets={daiResponse.details.metRequirements}
                  fieldName="requirement"
                  hasCopy
                />
              </Box>
            )}

            {hasRequirements && hasGuidelines && (
              <Divider className={classes.divider} />
            )}

            {hasEvidences && (
              <Box className={classes.requirementsSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Evidências
                </Typography>

                <MetList
                  mets={daiResponse.details.metEvidences}
                  fieldName="evidence"
                  hasCopy
                />
              </Box>
            )}

            {hasGuidelines && (
              <Box>
                <Box className={classes.guidelinesHeader}>
                  <AlertCircleIcon
                    size={20}
                    className={classes.guidelinesIcon}
                  />
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Diretrizes de Melhoria
                  </Typography>
                </Box>
                <List className={classes.guidelinesList}>
                  {daiResponse.details.improvementGuidelines.map(
                    (guideline, index) => (
                      <ListItem key={index} className={classes.guidelineItem}>
                        <ListItemIcon className={classes.guidelineNumber}>
                          <Box
                            className={clsx(
                              classes.guidelineIcon,
                              classes.containerNumberText,
                            )}
                          >
                            <Typography
                              variant="body2"
                              className={classes.numberText}
                            >
                              {index + 1}
                            </Typography>
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={guideline}
                          classes={{ primary: classes.guidelineText }}
                        />
                      </ListItem>
                    ),
                  )}
                </List>
              </Box>
            )}
          </Box>
          {(daiResponse?.daiResponseId || defaultDaiResponse?.id) && (
            <Box
              display="flex"
              justifyContent="flex-end"
              gridGap={theme.spacing(1)}
            >
              <DownloadStatement
                governanceProjectActivity={governanceProjectActivity}
                governanceProjectId={governanceProjectId}
              />
              <Button
                startIcon={<ThumbsUpIcon size={16} />}
                onClick={() => setOpenFeedback(true)}
              >
                Avaliar resposta
              </Button>
            </Box>
          )}
        </Box>
      )}

      <FeedbackDialog
        open={openFeedback}
        setOpen={setOpenFeedback}
        governanceProjectId={governanceProjectId}
        governanceProjectActivityId={governanceProjectActivity?.id}
        daiResponseId={governanceProjectActivity?.daiResponse?.id}
      />
    </Dai.Card>
  )
}

export default ActivityAnalysisDai
